import { useState } from "react";
import {
  InfiniteListBase,
  useListContext,
  useTranslate,
  useRedirect,
} from "ra-core";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { MobileListHeader } from "../layout/MobileListHeader";
import { MobileContent } from "../layout/MobileContent";
import { InfinitePagination } from "../misc/InfinitePagination";
import { CompanyAvatar } from "./CompanyAvatar";
import { CompanyCreateSheet } from "./CompanyCreateSheet";
import { useConfigurationContext } from "../root/ConfigurationContext";
import type { Company } from "../types";

export const MobileCompanyList = () => (
  <InfiniteListBase
    perPage={50}
    sort={{ field: "name", order: "ASC" }}
  >
    <MobileCompanyListLayout />
  </InfiniteListBase>
);

const MobileCompanyListLayout = () => {
  const translate = useTranslate();
  const { data, isPending, error } = useListContext<Company>();
  const [createOpen, setCreateOpen] = useState(false);

  return (
    <>
      <MobileListHeader
        title={translate("resources.companies.name", { smart_count: 2 })}
        onCreateClick={() => setCreateOpen(true)}
      />
      <CompanyCreateSheet open={createOpen} onOpenChange={setCreateOpen} />
      <MobileContent>
        {isPending ? (
          <MobileCompanySkeleton />
        ) : error ? (
          <p className="text-sm text-destructive">
            {translate("ra.message.error")}
          </p>
        ) : data?.length ? (
          <div className="space-y-3">
            {data.map((company) => (
              <MobileCompanyCard key={company.id} company={company} />
            ))}
          </div>
        ) : (
          <div className="py-16 text-center space-y-2">
            <p className="text-lg font-semibold">
              {translate("resources.companies.empty.title")}
            </p>
            <Button variant="outline" size="sm" onClick={() => setCreateOpen(true)}>
              {translate("resources.companies.action.new", { _: "Новая компания" })}
            </Button>
          </div>
        )}
        <div className="flex justify-center mt-4">
          <InfinitePagination />
        </div>
      </MobileContent>
    </>
  );
};

const MobileCompanyCard = ({ company }: { company: Company }) => {
  const redirect = useRedirect();
  const { companySectors } = useConfigurationContext();
  const sector = companySectors.find((s) => s.value === company.sector);

  return (
    <Card
      className="py-3 cursor-pointer active:opacity-70"
      role="button"
      tabIndex={0}
      onClick={() => redirect(`/companies/${company.id}/show`)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          redirect(`/companies/${company.id}/show`);
        }
      }}
    >
      <CardContent className="px-4">
        <div className="flex items-center gap-3">
          <CompanyAvatar record={company} width={40} height={40} />
          <div className="flex-1 min-w-0">
            <p className="font-medium text-sm truncate">{company.name}</p>
            {sector && (
              <p className="text-xs text-muted-foreground truncate">
                {sector.label}
              </p>
            )}
            <div className="flex gap-3 mt-1 text-xs text-muted-foreground">
              {company.nb_contacts ? (
                <span>{company.nb_contacts} контакт(ов)</span>
              ) : null}
              {company.nb_deals ? (
                <span>{company.nb_deals} сделок</span>
              ) : null}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const MobileCompanySkeleton = () => (
  <div className="space-y-3">
    {Array.from({ length: 5 }).map((_, i) => (
      <Skeleton key={i} className="h-20 w-full rounded-xl" />
    ))}
  </div>
);
